'use client';

import { useCallback, useMemo } from 'react';
import posthog from 'posthog-js';
import { useSession } from 'next-auth/react';
import { useAtomValue } from 'jotai';
import { jwtDecode } from 'jwt-decode';
import { acceptedCookiePolicyAtom } from '~/atoms';
import { CookieTypeEnum } from '~/types-and-enums/cookieTypes';
import { SessionUserType } from '~/types-and-enums/trackingTypes';
import { isProductionEnv } from '~/utils/envConfig';
import { checkUserIsEmployee } from '~/utils/generalUtils';

type EventProperties = Record<string, string | number | boolean | null | undefined>;

type JwtClaims = {
  contact_id?: string;
  account_id?: string;
  account_name?: string;
  finished_setup?: boolean;
  user_id?: string;
};

const isPostHogReady = () =>
  typeof window !== 'undefined' && !!posthog.__loaded && !posthog.has_opted_out_capturing();

const isProd = () => isProductionEnv();
const isDev = () => !isProd();

export const usePostHog = () => {
  const acceptedCookiePolicy = useAtomValue(acceptedCookiePolicyAtom);
  const hasAnalyticsConsent = acceptedCookiePolicy.includes(CookieTypeEnum.Analytics);

  const { data: session } = useSession();
  const user = session?.user as SessionUserType;

  const isEnabled = hasAnalyticsConsent && isPostHogReady();

  const accessToken = user?.access_token;

  const jwtClaims = useMemo<JwtClaims>(() => {
    try {
      return accessToken ? jwtDecode<JwtClaims>(accessToken) : {};
    } catch {
      return {};
    }
  }, [accessToken]);

  const getUserContext = useCallback((): EventProperties => {
    if (!user) return {};
    const { id, email, name } = user;
    const isEmployee = checkUserIsEmployee(email);
    return {
      actor_user_id: id ?? null,
      actor_email: email ?? null,
      actor_name: name ?? null,
      actor_user_type: isEmployee ? 'employee' : 'buyer',
      actor_salesforce_contact_id: jwtClaims.contact_id ?? null,
    };
  }, [user, jwtClaims]);

  const captureEvent = useCallback(
    (eventName: string, properties?: EventProperties) => {
      const payload = { ...getUserContext(), ...properties };
      if (isDev())
        console.warn('[PostHog DEV] event:', eventName, JSON.stringify(payload, null, 2));
      if (!isProd() || !isEnabled) return;
      try {
        posthog.capture(eventName, payload);
      } catch (err) {
        console.error('[PostHog] captureEvent error:', err);
      }
    },
    [isEnabled, getUserContext],
  );

  const capturePageView = useCallback(
    (url: string) => {
      const payload = {
        $current_url: url,
        page_title: typeof document !== 'undefined' ? document.title : undefined,
        page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
        ...getUserContext(),
      };
      if (isDev()) console.warn('[PostHog DEV] $pageview:', url, payload);
      if (!isProd() || !isEnabled) return;
      try {
        posthog.capture('$pageview', payload);
      } catch (err) {
        console.error('[PostHog] capturePageView error:', err);
      }
    },
    [isEnabled, getUserContext],
  );

  const identifyUser = useCallback(
    (userId: string, traits?: EventProperties) => {
      // if (isDev()) console.warn('[PostHog DEV] identify:', userId, traits);
      if (!isProd() || !isEnabled) return;
      try {
        posthog.identify(userId, traits);
      } catch (err) {
        console.error('[PostHog] identifyUser error:', err);
      }
    },
    [isEnabled],
  );

  const resetUser = useCallback(() => {
    if (isDev()) console.warn('[PostHog DEV] reset user');
    if (!isProd() || !isPostHogReady()) return;
    try {
      posthog.reset();
    } catch (err) {
      console.error('[PostHog] resetUser error:', err);
    }
  }, []);

  return { captureEvent, capturePageView, identifyUser, resetUser, isEnabled };
};
